import React, { useState, useEffect } from 'react';
import { 
  X, Lock, Users, Building2, BarChart3, Mail, Phone, Globe,
  Trash2, Save, LogOut, LayoutDashboard, Share2,
  Plus, Linkedin, MessageCircle, Instagram, Twitter, Facebook, Youtube,
  TrendingUp, CheckCircle, Clock, AlertCircle, FolderOpen, FileText,
  Target, Image, Signature, Briefcase, Landmark, Megaphone, Bell, Sparkles, ClipboardList
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { CRMPanel } from './CRMPanel';
import { TeamTab } from './admin/TeamTab';
import { ProjectsTab } from './admin/ProjectsTab';
import { LogosTab } from './admin/LogosTab';
import { StatsTab } from './admin/StatsTab';
import { AnalyticsTab } from './admin/AnalyticsTab';
import { LegalPagesTab } from './admin/LegalPagesTab';
import { ComplaintsPolicyTab } from './admin/ComplaintsPolicyTab';
import { EmailTemplatesTab } from './admin/EmailTemplatesTab';
import { SignaturesTab } from './admin/SignaturesTab';
import { BusinessPlanTab } from './admin/BusinessPlanTab';
import { DealsTab } from './admin/DealsTab';
import { DealAnalyticsTab } from './admin/DealAnalyticsTab';
import { BrandAssetsTab } from './admin/BrandAssetsTab';
import { GlobalFootprintTab } from './admin/GlobalFootprintTab';
import { ConversionAttentionCard } from './admin/ConversionAttentionCard';
import { SendGridHealthPill } from './admin/SendGridHealthPill';
import { PopupTab } from './admin/PopupTab';
import { AutomationTab } from './admin/AutomationTab';
import { NotificationsTab } from './admin/NotificationsTab';
import { BespokeSourcingTab } from './admin/BespokeSourcingTab';
import { BespokeBriefsTab } from './admin/BespokeBriefsTab';
import { JourneyContentTab } from './admin/JourneyContentTab';
import { InsightsTab } from './admin/InsightsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, color }) => {
  const colors = {
    blue: 'bg-blue-500/20 text-blue-400',
    yellow: 'bg-yellow-500/20 text-yellow-400',
    orange: 'bg-orange-500/20 text-orange-400',
    green: 'bg-green-500/20 text-green-400',
    purple: 'bg-purple-500/20 text-purple-400',
    teal: 'bg-teal-500/20 text-teal-400'
  };
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl text-white font-light">{value}</p>
      <p className="text-white/50 text-sm">{label}</p>
    </div>
  );
};

const FormField = ({ label, value, onChange, type = 'text', icon: Icon, className = '', placeholder = '' }) => (
  <div className={`bg-white/5 border border-white/10 rounded-lg p-4 ${className}`}>
    <label className="text-white/50 text-sm block mb-2 flex items-center gap-2">
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white/5 border-white/10 text-white"
      placeholder={placeholder}
    />
  </div>
);

export const AdminPanel = ({ isOpen, onClose }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [dashboardStats, setDashboardStats] = useState({
    total_inquiries: 0, new_inquiries: 0, contacted: 0,
    converted: 0, team_members: 0, projects: 0
  });
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({
    properties_acquired: 50, capital_deployed: 10,
    average_roi: 25, investors_served: 100
  });
  const [companyInfo, setCompanyInfo] = useState({
    company_name: 'Black Star PI', tagline: 'Property Investment',
    slogan: 'Acquire. Optimise. Multiply.', description: '',
    address_line1: 'Third Floor, 3 Hill Street',
    address_line2: 'New Town, Edinburgh',
    address_postcode: 'EH2 3JP, United Kingdom'
  });
  const [contactDetails, setContactDetails] = useState({
    email: 'info@black-starpi.com', phone: '+44 131 381 1989',
    mobile: '', website: 'black-starpi.com',
    show_email: true, show_phone: true, show_mobile: true, show_website: true,
  });
  const [socialLinks, setSocialLinks] = useState({
    facebook: 'https://facebook.com', instagram: 'https://instagram.com',
    twitter: 'https://x.com', linkedin: 'https://linkedin.com',
    youtube: 'https://youtube.com', whatsapp: 'https://wa.me/'
  });
  const [teamMembers, setTeamMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [complaintsPolicy, setComplaintsPolicy] = useState({ pdf_url: '', pdf_filename: '' });
  const [logos, setLogos] = useState([]);
  const [sectionSettings, setSectionSettings] = useState({ team_section_enabled: true });
  const [dealSourcingCfg, setDealSourcingCfg] = useState({
    enabled: true,
    redirect_enabled: false,
    redirect_url: 'https://www.goldstonesourcing.com',
  });
  const [dealSourcingSaving, setDealSourcingSaving] = useState(false);
  const [analyticsSettings, setAnalyticsSettings] = useState({
    ga4_measurement_id: '', plausible_domain: '',
    ga4_enabled: false, plausible_enabled: false
  });

  const getStoredPassword = () => localStorage.getItem('adminPassword');

  const handleLogin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('adminPassword', password);
        toast.success('Login successful');
        fetchAllData();
      } else {
        toast.error('Invalid password');
      }
    } catch (error) {
      toast.error('Login failed');
    }
  };

  const fetchAllData = async () => {
    const storedPassword = getStoredPassword();
    if (!storedPassword) return;
    setLoading(true);
    try {
      const endpoints = [
        { url: `/api/admin/dashboard-stats?password=${storedPassword}`, setter: setDashboardStats },
        { url: `/api/admin/submissions?password=${storedPassword}`, setter: setSubmissions },
        { url: `/api/admin/stats?password=${storedPassword}`, setter: setStats },
        { url: `/api/admin/company-info?password=${storedPassword}`, setter: setCompanyInfo },
        { url: `/api/admin/contact-details?password=${storedPassword}`, setter: setContactDetails },
        { url: `/api/admin/social-links?password=${storedPassword}`, setter: setSocialLinks },
        { url: `/api/admin/team?password=${storedPassword}`, setter: setTeamMembers },
        { url: `/api/admin/projects?password=${storedPassword}`, setter: setProjects },
        { url: `/api/admin/complaints-policy?password=${storedPassword}`, setter: setComplaintsPolicy },
        { url: `/api/admin/logos?password=${storedPassword}`, setter: setLogos },
        { url: `/api/admin/section-settings?password=${storedPassword}`, setter: setSectionSettings },
        { url: `/api/admin/deal-sourcing-settings?password=${storedPassword}`, setter: (d) => setDealSourcingCfg({
          enabled: d?.enabled !== false,
          redirect_enabled: Boolean(d?.redirect_enabled),
          redirect_url: d?.redirect_url || 'https://www.goldstonesourcing.com',
        }) },
        { url: `/api/admin/analytics-settings?password=${storedPassword}`, setter: setAnalyticsSettings }
      ];
      await Promise.all(endpoints.map(async ({ url, setter }) => {
        try {
          const res = await fetch(`${API_URL}${url}`);
          if (res.ok) setter(await res.json());
        } catch (e) {
          console.error(`Error fetching ${url}:`, e);
        }
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  // --- Save/Action Handlers ---
  const updateSubmissionStatus = async (id, status) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/submissions/${id}/status?status=${status}&password=${getStoredPassword()}`,
        { method: 'PATCH' }
      );
      if (response.ok) { toast.success('Status updated'); fetchAllData(); }
    } catch (error) { toast.error('Failed to update status'); }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/submissions/${id}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (response.ok) { toast.success('Submission deleted'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete'); }
  };

  const saveStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/stats?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stats)
      });
      if (response.ok) toast.success('Stats updated');
    } catch (error) { toast.error('Failed to update stats'); }
  };

  const saveCompanyInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/company-info?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyInfo)
      });
      if (response.ok) toast.success('Company info updated');
    } catch (error) { toast.error('Failed to update company info'); }
  };

  const saveDealSourcingCfg = async (patch = {}) => {
    setDealSourcingSaving(true);
    const prev = dealSourcingCfg;
    const next = { ...prev, ...patch };
    setDealSourcingCfg(next); // optimistic
    try {
      const res = await fetch(
        `${API_URL}/api/admin/deal-sourcing-settings?password=${getStoredPassword()}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(next),
        }
      );
      if (!res.ok) throw new Error('save failed');
      if ('redirect_enabled' in patch) {
        toast.success(patch.redirect_enabled ? 'Redirect enabled — visitors will be forwarded' : 'Redirect disabled — internal deals restored');
      } else {
        toast.success('Deal Sourcing settings saved');
      }
    } catch {
      setDealSourcingCfg(prev); // revert
      toast.error('Failed to save Deal Sourcing settings');
    } finally {
      setDealSourcingSaving(false);
    }
  };

  const saveContactDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/contact-details?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactDetails)
      });
      if (response.ok) toast.success('Contact details updated');
    } catch (error) { toast.error('Failed to update contact details'); }
  };

  const saveSocialLinks = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/social-links?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(socialLinks)
      });
      if (response.ok) toast.success('Social links updated');
    } catch (error) { toast.error('Failed to update social links'); }
  };

  const saveComplaintsPolicy = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/complaints-policy?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(complaintsPolicy)
      });
      if (response.ok) toast.success('Complaints policy updated');
    } catch (error) { toast.error('Failed to update complaints policy'); }
  };

  const saveTeamMember = async (member) => {
    try {
      const url = member.id 
        ? `${API_URL}/api/admin/team/${member.id}?password=${getStoredPassword()}`
        : `${API_URL}/api/admin/team?password=${getStoredPassword()}`;
      const response = await fetch(url, {
        method: member.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(member)
      });
      if (response.ok) {
        toast.success(member.id ? 'Team member updated' : 'Team member added');
        fetchAllData();
      }
    } catch (error) { toast.error('Failed to save team member'); }
  };

  const deleteTeamMember = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/team/${id}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (response.ok) { toast.success('Team member deleted'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete team member'); }
  };

  const toggleTeamSection = async () => {
    try {
      const newValue = !sectionSettings.team_section_enabled;
      const response = await fetch(`${API_URL}/api/admin/section-settings?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_section_enabled: newValue })
      });
      if (response.ok) {
        setSectionSettings({ ...sectionSettings, team_section_enabled: newValue });
        toast.success(`Team section ${newValue ? 'enabled' : 'disabled'}`);
      }
    } catch (error) { toast.error('Failed to update section settings'); }
  };

  const saveAnalyticsSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics-settings?password=${getStoredPassword()}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analyticsSettings)
      });
      if (response.ok) toast.success('Analytics settings saved. Refresh the page to apply changes.');
    } catch (error) { toast.error('Failed to save analytics settings'); }
  };

  const saveProject = async (project) => {
    try {
      const url = project.id 
        ? `${API_URL}/api/admin/projects/${project.id}?password=${getStoredPassword()}`
        : `${API_URL}/api/admin/projects?password=${getStoredPassword()}`;
      const response = await fetch(url, {
        method: project.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(project)
      });
      if (response.ok) {
        toast.success(project.id ? 'Project updated' : 'Project added');
        fetchAllData();
      }
    } catch (error) { toast.error('Failed to save project'); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/projects/${id}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (response.ok) { toast.success('Project deleted'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete project'); }
  };

  const saveLogo = async (logo) => {
    try {
      const url = logo.id 
        ? `${API_URL}/api/admin/logos/${logo.id}?password=${getStoredPassword()}`
        : `${API_URL}/api/admin/logos?password=${getStoredPassword()}`;
      const response = await fetch(url, {
        method: logo.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logo)
      });
      if (response.ok) {
        toast.success(logo.id ? 'Logo updated' : 'Logo added');
        fetchAllData();
      }
    } catch (error) { toast.error('Failed to save logo'); }
  };

  const deleteLogo = async (id) => {
    if (!window.confirm('Delete this logo?')) return;
    try {
      const response = await fetch(
        `${API_URL}/api/admin/logos/${id}?password=${getStoredPassword()}`,
        { method: 'DELETE' }
      );
      if (response.ok) { toast.success('Logo deleted'); fetchAllData(); }
    } catch (error) { toast.error('Failed to delete logo'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminPassword');
    setIsAuthenticated(false);
    setPassword('');
    onClose();
  };

  useEffect(() => {
    const storedPassword = localStorage.getItem('adminPassword');
    if (storedPassword && isOpen) {
      setPassword(storedPassword);
      setIsAuthenticated(true);
      fetchAllData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'business-plan', label: 'Business Plan', icon: Briefcase },
    { id: 'deals', label: 'Deal Sourcing', icon: Landmark },
    { id: 'deal-analytics', label: 'Deal Analytics', icon: BarChart3 },
    { id: 'bespoke', label: 'Bespoke Sourcing', icon: Sparkles },
    { id: 'bespoke-briefs', label: 'Bespoke Briefs', icon: ClipboardList },
    { id: 'journey-content', label: 'Journey Deck', icon: FileText },
    { id: 'insights', label: 'Insights', icon: Sparkles },
    { id: 'popup', label: 'Popup Banner', icon: Megaphone },
    { id: 'crm', label: 'CRM', icon: Target },
    { id: 'automation', label: 'Automation', icon: TrendingUp },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'inquiries', label: 'Inquiries', icon: Mail },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'projects', label: 'Projects', icon: FolderOpen },
    { id: 'logos', label: 'Affiliations', icon: Image },
    { id: 'company', label: 'Company Info', icon: Building2 },
    { id: 'contact', label: 'Contact', icon: Phone },
    { id: 'social', label: 'Social Links', icon: Share2 },
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
    { id: 'emails', label: 'Email Templates', icon: Mail },
    { id: 'signatures', label: 'Signatures', icon: Signature },
    { id: 'brand-assets', label: 'Brand Assets', icon: Image },
    { id: 'global-footprint', label: 'Global Footprint', icon: Globe },
    { id: 'pages', label: 'Legal Pages', icon: FileText },
    { id: 'complaints', label: 'Complaints Policy', icon: FileText }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-500/20 text-blue-400',
      contacted: 'bg-yellow-500/20 text-yellow-400',
      converted: 'bg-green-500/20 text-green-400',
      closed: 'bg-gray-500/20 text-gray-400'
    };
    return styles[status] || styles.new;
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/90 cursor-pointer" onClick={onClose} />
      
      <div className="w-full max-w-6xl bg-[#0a1a14] border-l border-white/10 h-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-[#0a1a14]/95">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37]/20 rounded-lg flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="text-xl font-light text-white">Admin Panel</h2>
              <p className="text-xs text-white/50">Black Star PI Management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isAuthenticated && (
              <Button onClick={handleLogout} variant="ghost" className="text-white/60 hover:text-white">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded" data-testid="admin-close-btn">
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>
        </div>

        {!isAuthenticated ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="w-full max-w-sm space-y-6">
              <div className="text-center">
                <Lock className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="text-xl text-white mb-2">Admin Access</h3>
                <p className="text-white/50 text-sm">Enter password to continue</p>
              </div>
              <div className="space-y-4">
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="admin-password-input"
                />
                <Button onClick={handleLogin} className="w-full bg-[#D4AF37] text-black hover:bg-[#B8941E]" data-testid="admin-login-btn">
                  Login
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex overflow-hidden">
            {/* Sidebar */}
            <div className="w-52 border-r border-white/10 p-3 space-y-1 overflow-y-auto">
              {tabs.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  data-testid={`admin-tab-${id}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                    activeTab === id ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'text-white/60 hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading && <div className="text-center text-white/50 py-8">Loading...</div>}

              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && !loading && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="text-lg text-white">Dashboard Overview</h3>
                    <SendGridHealthPill getStoredPassword={getStoredPassword} />
                  </div>

                  {/* Deal Sourcing Redirect */}
                  <div
                    className={`relative overflow-hidden rounded-xl border transition-colors ${
                      dealSourcingCfg.redirect_enabled
                        ? 'border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/[0.06] via-transparent to-transparent'
                        : 'border-white/10 bg-white/[0.02]'
                    }`}
                    data-testid="deal-sourcing-master-switch-card"
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          dealSourcingCfg.redirect_enabled ? 'bg-[#D4AF37]/15 text-[#D4AF37]' : 'bg-white/5 text-white/40'
                        }`}>
                          <Share2 className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-white text-[15px] font-medium">Deal Sourcing Redirect</h4>
                            <span className={`text-[9px] tracking-[0.18em] font-bold px-2 py-0.5 rounded-full uppercase border ${
                              dealSourcingCfg.redirect_enabled
                                ? 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/25'
                                : 'bg-white/5 text-white/40 border-white/10'
                            }`} data-testid="deal-sourcing-status-pill">
                              {dealSourcingCfg.redirect_enabled ? 'Forwarding' : 'Internal'}
                            </span>
                          </div>
                          <p className="text-white/50 text-xs mt-1.5 leading-relaxed max-w-lg">
                            When enabled, every Deal Sourcing click on the site is forwarded to the URL below —
                            the internal <span className="text-white/70">/deals</span> pages become a soft redirect.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => saveDealSourcingCfg({ redirect_enabled: !dealSourcingCfg.redirect_enabled })}
                          disabled={dealSourcingSaving}
                          data-testid="deal-sourcing-toggle"
                          aria-pressed={dealSourcingCfg.redirect_enabled}
                          aria-label="Toggle Deal Sourcing redirect"
                          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 ${
                            dealSourcingCfg.redirect_enabled
                              ? 'bg-[#D4AF37] border-[#D4AF37]'
                              : 'bg-white/10 border-white/15'
                          }`}
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                              dealSourcingCfg.redirect_enabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="mt-4 pl-14 space-y-2">
                        <label className="text-[10px] uppercase tracking-[0.15em] text-white/45">Redirect URL</label>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={dealSourcingCfg.redirect_url}
                            onChange={(e) => setDealSourcingCfg({ ...dealSourcingCfg, redirect_url: e.target.value })}
                            placeholder="https://www.goldstonesourcing.com"
                            className="flex-1 bg-black/25 border border-white/10 rounded-md px-3 py-2 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#D4AF37]/40 focus:bg-black/40 transition-colors"
                            data-testid="deal-sourcing-redirect-url"
                          />
                          <button
                            type="button"
                            onClick={() => saveDealSourcingCfg({})}
                            disabled={dealSourcingSaving}
                            className="px-4 py-2 rounded-md bg-[#D4AF37] text-black text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#B8941E] disabled:opacity-50 transition-colors"
                            data-testid="deal-sourcing-save-url"
                          >
                            Save
                          </button>
                        </div>
                        {dealSourcingCfg.redirect_enabled && dealSourcingCfg.redirect_url && (
                          <p className="text-[11px] text-white/40 pt-1">
                            Live now — clicks route to{' '}
                            <a href={dealSourcingCfg.redirect_url} target="_blank" rel="noopener noreferrer"
                              className="text-[#D4AF37] hover:underline">{dealSourcingCfg.redirect_url}</a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard icon={Mail} label="Total Inquiries" value={dashboardStats.total_inquiries} color="blue" />
                    <StatCard icon={AlertCircle} label="New Inquiries" value={dashboardStats.new_inquiries} color="yellow" />
                    <StatCard icon={Clock} label="Contacted" value={dashboardStats.contacted} color="orange" />
                    <StatCard icon={CheckCircle} label="Converted" value={dashboardStats.converted} color="green" />
                    <StatCard icon={Users} label="Team Members" value={dashboardStats.team_members} color="purple" />
                    <StatCard icon={FolderOpen} label="Projects" value={dashboardStats.projects} color="teal" />
                  </div>
                  {/* Conversion Attention — auto-hidden when no deals qualify */}
                  <ConversionAttentionCard
                    getStoredPassword={getStoredPassword}
                    onOpenAnalytics={() => setActiveTab('deal-analytics')}
                  />

                  <div className="mt-8">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-white/80">Recent Inquiries</h4>
                      <button onClick={() => setActiveTab('inquiries')} className="text-[#D4AF37] text-sm hover:underline">View All</button>
                    </div>
                    <div className="space-y-2">
                      {submissions.slice(0, 3).map((sub) => (
                        <div key={sub.id} className="bg-white/5 rounded-lg p-3 flex items-center justify-between">
                          <div>
                            <span className="text-white text-sm">{sub.name}</span>
                            <span className="text-white/40 text-xs ml-2">{sub.investment_interest}</span>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(sub.status)}`}>{sub.status}</span>
                        </div>
                      ))}
                      {submissions.length === 0 && (
                        <p className="text-white/40 text-sm text-center py-4">No inquiries yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* CRM Tab */}
              {activeTab === 'crm' && !loading && (
                <CRMPanel getStoredPassword={getStoredPassword} />
              )}

              {/* Automation Tab */}
              {activeTab === 'automation' && !loading && (
                <AutomationTab getStoredPassword={getStoredPassword} />
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && !loading && (
                <NotificationsTab getStoredPassword={getStoredPassword} />
              )}

              {/* Inquiries Tab */}
              {activeTab === 'inquiries' && !loading && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg text-white">Contact Inquiries</h3>
                    <span className="text-sm text-white/50">{submissions.length} total</span>
                  </div>
                  {submissions.length === 0 ? (
                    <div className="text-center text-white/50 py-8">No submissions yet</div>
                  ) : (
                    <div className="space-y-3">
                      {submissions.map((sub) => (
                        <div key={sub.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="text-white font-medium">{sub.name}</h4>
                              <p className="text-white/50 text-sm">{sub.email}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(sub.status)}`}>{sub.status}</span>
                          </div>
                          <p className="text-[#D4AF37] text-sm mb-2">{sub.investment_interest}</p>
                          <p className="text-white/60 text-sm mb-3">{sub.message || 'No message'}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-white/30 text-xs">{new Date(sub.created_at).toLocaleString()}</span>
                            <div className="flex items-center gap-2">
                              <select
                                value={sub.status}
                                onChange={(e) => updateSubmissionStatus(sub.id, e.target.value)}
                                className="bg-white/5 border border-white/10 text-white text-xs rounded px-2 py-1"
                              >
                                <option value="new">New</option>
                                <option value="contacted">Contacted</option>
                                <option value="converted">Converted</option>
                                <option value="closed">Closed</option>
                              </select>
                              <button onClick={() => deleteSubmission(sub.id)} className="p-1 hover:bg-red-500/20 rounded text-red-400">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Team Tab */}
              {activeTab === 'team' && !loading && (
                <TeamTab
                  teamMembers={teamMembers}
                  sectionSettings={sectionSettings}
                  toggleTeamSection={toggleTeamSection}
                  saveTeamMember={saveTeamMember}
                  deleteTeamMember={deleteTeamMember}
                />
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && !loading && (
                <ProjectsTab 
                  projects={projects}
                  saveProject={saveProject}
                  deleteProject={deleteProject}
                  fetchAllData={fetchAllData}
                  getStoredPassword={getStoredPassword}
                />
              )}

              {/* Affiliations Tab */}
              {activeTab === 'logos' && !loading && (
                <LogosTab logos={logos} saveLogo={saveLogo} deleteLogo={deleteLogo} />
              )}

              {/* Company Info Tab */}
              {activeTab === 'company' && !loading && (
                <div className="space-y-6">
                  <h3 className="text-lg text-white mb-6">Company Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Company Name" value={companyInfo.company_name} onChange={(v) => setCompanyInfo({...companyInfo, company_name: v})} />
                    <FormField label="Tagline" value={companyInfo.tagline} onChange={(v) => setCompanyInfo({...companyInfo, tagline: v})} />
                    <FormField label="Slogan" value={companyInfo.slogan} onChange={(v) => setCompanyInfo({...companyInfo, slogan: v})} className="md:col-span-2" />
                    <FormField label="Address Line 1" value={companyInfo.address_line1} onChange={(v) => setCompanyInfo({...companyInfo, address_line1: v})} />
                    <FormField label="Address Line 2" value={companyInfo.address_line2} onChange={(v) => setCompanyInfo({...companyInfo, address_line2: v})} />
                    <FormField label="Postcode & Country" value={companyInfo.address_postcode} onChange={(v) => setCompanyInfo({...companyInfo, address_postcode: v})} className="md:col-span-2" />
                  </div>
                  <Button onClick={saveCompanyInfo} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]">
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                </div>
              )}

              {/* Contact Details Tab */}
              {activeTab === 'contact' && !loading && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg text-white">Contact Details</h3>
                    <p className="text-[12px] text-white/50 mt-1 max-w-2xl">
                      Each field has a per-row visibility toggle on the right — turn off to hide that line
                      from the public footer while keeping the data saved here.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'email',   label: 'Email',            icon: Mail,  placeholder: '' },
                      { key: 'phone',   label: 'Phone (Landline)', icon: Phone, placeholder: '' },
                      { key: 'mobile',  label: 'Mobile',           icon: Phone, placeholder: '+44 7XXX XXXXXX' },
                      { key: 'website', label: 'Website',          icon: Globe, placeholder: '' },
                    ].map(({ key, label, icon, placeholder }) => {
                      const showKey = `show_${key}`;
                      const visible = contactDetails[showKey] !== false;
                      return (
                        <div key={key} className="space-y-1.5" data-testid={`contact-field-${key}`}>
                          <div className="flex items-center justify-between gap-3">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-white/55">{label}</span>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid={`contact-show-${key}-toggle`}>
                              <input
                                type="checkbox"
                                checked={visible}
                                onChange={(e) => setContactDetails({ ...contactDetails, [showKey]: e.target.checked })}
                                className="accent-[#D4AF37] w-3.5 h-3.5"
                              />
                              <span className={`text-[10px] uppercase tracking-[0.12em] font-semibold ${visible ? 'text-[#D4AF37]' : 'text-white/40'}`}>
                                {visible ? 'Visible' : 'Hidden'}
                              </span>
                            </label>
                          </div>
                          <FormField
                            label=""
                            value={contactDetails[key]}
                            onChange={(v) => setContactDetails({ ...contactDetails, [key]: v })}
                            icon={icon}
                            placeholder={placeholder}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <Button onClick={saveContactDetails} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]">
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                </div>
              )}

              {/* Social Links Tab */}
              {activeTab === 'social' && !loading && (
                <div className="space-y-6">
                  <h3 className="text-lg text-white mb-6">Social Media Links</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField label="Facebook" value={socialLinks.facebook} onChange={(v) => setSocialLinks({...socialLinks, facebook: v})} icon={Facebook} />
                    <FormField label="Instagram" value={socialLinks.instagram} onChange={(v) => setSocialLinks({...socialLinks, instagram: v})} icon={Instagram} />
                    <FormField label="Twitter / X" value={socialLinks.twitter} onChange={(v) => setSocialLinks({...socialLinks, twitter: v})} icon={Twitter} />
                    <FormField label="LinkedIn" value={socialLinks.linkedin} onChange={(v) => setSocialLinks({...socialLinks, linkedin: v})} icon={Linkedin} />
                    <FormField label="YouTube" value={socialLinks.youtube} onChange={(v) => setSocialLinks({...socialLinks, youtube: v})} icon={Youtube} />
                    <FormField label="WhatsApp" value={socialLinks.whatsapp} onChange={(v) => setSocialLinks({...socialLinks, whatsapp: v})} icon={MessageCircle} />
                  </div>
                  <Button onClick={saveSocialLinks} className="bg-[#D4AF37] text-black hover:bg-[#B8941E]">
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                </div>
              )}

              {/* Stats Tab */}
              {activeTab === 'stats' && !loading && (
                <StatsTab
                  stats={stats}
                  setStats={setStats}
                  saveStats={saveStats}
                  getStoredPassword={getStoredPassword}
                />
              )}

              {/* Analytics Tab */}
              {activeTab === 'analytics' && !loading && (
                <AnalyticsTab
                  analyticsSettings={analyticsSettings}
                  setAnalyticsSettings={setAnalyticsSettings}
                  saveAnalyticsSettings={saveAnalyticsSettings}
                />
              )}

              {/* Email Templates Tab */}
              {activeTab === 'emails' && !loading && (
                <EmailTemplatesTab getStoredPassword={getStoredPassword} />
              )}

              {/* Signatures Tab */}
              {activeTab === 'signatures' && !loading && (
                <SignaturesTab getStoredPassword={getStoredPassword} />
              )}

              {/* Brand Assets Tab */}
              {activeTab === 'brand-assets' && !loading && (
                <BrandAssetsTab getStoredPassword={getStoredPassword} />
              )}

              {/* Global Footprint Tab */}
              {activeTab === 'global-footprint' && !loading && (
                <GlobalFootprintTab getStoredPassword={getStoredPassword} />
              )}

              {/* Business Plan Tab */}
              {activeTab === 'business-plan' && !loading && (
                <BusinessPlanTab getStoredPassword={getStoredPassword} />
              )}

              {/* Deals Tab */}
              {activeTab === 'deals' && !loading && (
                <DealsTab getStoredPassword={getStoredPassword} />
              )}

              {/* Deal Analytics Tab */}
              {activeTab === 'deal-analytics' && !loading && (
                <DealAnalyticsTab getStoredPassword={getStoredPassword} />
              )}

              {/* Bespoke Sourcing Tab */}
              {activeTab === 'bespoke' && !loading && (
                <BespokeSourcingTab getStoredPassword={getStoredPassword} />
              )}

              {/* Bespoke Briefs Pipeline Tab */}
              {activeTab === 'bespoke-briefs' && !loading && (
                <BespokeBriefsTab getStoredPassword={getStoredPassword} />
              )}
              {activeTab === 'journey-content' && !loading && (
                <JourneyContentTab getStoredPassword={getStoredPassword} />
              )}

              {/* Insights Tab */}
              {activeTab === 'insights' && !loading && (
                <InsightsTab getStoredPassword={getStoredPassword} />
              )}

              {/* Popup Tab */}
              {activeTab === 'popup' && !loading && (
                <PopupTab getStoredPassword={getStoredPassword} />
              )}

              {/* Legal Pages Tab */}
              {activeTab === 'pages' && !loading && (
                <LegalPagesTab getStoredPassword={getStoredPassword} />
              )}

              {/* Complaints Policy Tab */}
              {activeTab === 'complaints' && !loading && (
                <ComplaintsPolicyTab 
                  complaintsPolicy={complaintsPolicy}
                  setComplaintsPolicy={setComplaintsPolicy}
                  saveComplaintsPolicy={saveComplaintsPolicy}
                  getStoredPassword={getStoredPassword}
                  fetchAllData={fetchAllData}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
